import React from "react";
import TaskStatusBadge from "./TaskStatusBadge";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import AddIcon from "@mui/icons-material/Add";
import Tooltip from "@mui/material/Tooltip";
import { getCardColor2 } from "../../../../helpers/helpers";
import { getInitials } from "../../requests/utilities/helpers";
import { windowLabel as sharedWindowLabel } from "../tasks_hub/taskHubUtils";

// Shared header for the manager and employee task modals: title + status,
// then ONE compact row holding the window, intraday cadence, and audience,
// plus attachment pills when the task has files. Kept intentionally short so
// the checklist below gets the vertical space.
//
// The status pill matches the Now list exactly (same component, same
// vocabulary), so a person sees one status everywhere.

function MetaChip({ children }) {
  return (
    <span className="inline-flex items-center text-xs font-semibold text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2.5 py-0.5 tabular-nums">
      {children}
    </span>
  );
}

function TeamTaskChip() {
  return (
    <Tooltip title="Anyone on the team with a matching role can complete this. It only needs to be done once.">
      <span className="inline-flex items-center text-xs font-semibold text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2.5 py-0.5">
        Team task · anyone eligible
      </span>
    </Tooltip>
  );
}

function MemberAvatars({ members, roleNames, resolveRoleId, cap = 7 }) {
  return (
    <span className="inline-flex items-center">
      {members.map((employee, index, array) => {
        if (array.length > cap + 1 && index >= cap) {
          if (index === cap) {
            return (
              <span
                key="more"
                className="bg-slate-400 text-white w-8 h-8 rounded-full flex items-center justify-center border-2 border-white -ml-1.5 text-[10px] font-semibold"
              >
                +{array.length - cap}
              </span>
            );
          }
          return null;
        }
        const role_id = resolveRoleId(employee);
        const cardColor = getCardColor2(role_id, roleNames, false);
        return (
          <Tooltip key={index} title={employee?.store_team_member?.full_name}>
            <span
              className={`${cardColor} text-white w-8 h-8 rounded-full shadow-sm flex items-center justify-center border-2 border-white -ml-1.5 first:ml-0 text-[10px] font-semibold`}
            >
              {getInitials(employee?.store_team_member?.full_name)}
            </span>
          </Tooltip>
        );
      })}
    </span>
  );
}

function PinButton({ onClick }) {
  return (
    <Tooltip title="Pin this task to specific team members">
      <span
        className="text-slate-500 bg-white border border-slate-200 hover:border-violet-400 w-7 h-7 rounded-full flex items-center justify-center cursor-pointer ml-1 transition-colors"
        onClick={onClick}
      >
        <AddIcon fontSize="small" />
      </span>
    </Tooltip>
  );
}

function TaskNameCardModalHeader(props) {
  const task = props.task || {};

  // The shared formatter, not a private one: a one-sided window ("From 9 AM")
  // and a window that starts at midnight both read "Anytime" under a truthiness
  // test, since 0 is falsy.
  const windowText = sharedWindowLabel(task);

  // ---- audience element (rendered inline in the meta row) ----
  let audience = null;
  if (props.permissionType === "employee") {
    if (props.isAssignedToEveryone) {
      audience = <TeamTaskChip />;
    } else if (props.plan_task_assignment_members?.length === 1) {
      const employee = props.plan_task_assignment_members[0];
      const cardColor = getCardColor2(employee?.role_id, props.roleNames, false);
      audience = (
        <span
          className={`${cardColor} text-white text-xs font-semibold px-2.5 py-0.5 rounded-full shadow-sm`}
        >
          {employee?.store_team_member?.full_name}
        </span>
      );
    } else if (props.plan_task_assignment_members?.length > 1) {
      audience = (
        <MemberAvatars
          members={props.plan_task_assignment_members}
          roleNames={props.roleNames}
          resolveRoleId={(e) => e?.role_id}
        />
      );
    } else {
      audience = (
        <span className="inline-flex items-center text-xs font-semibold text-violet-700 bg-violet-50 rounded-full px-2.5 py-0.5">
          Assigned to you
        </span>
      );
    }
  } else if (task?.is_intraday) {
    const intradaySlots = (task?.plan_task_assignments || []).filter(
      (a) => a.intraday_slot_index != null
    );
    const allCompleted =
      intradaySlots.length > 0 &&
      intradaySlots.every(
        (a) => a.status === "completed" || a.status === "completed_late"
      );
    const allMembers = intradaySlots.flatMap(
      (pta) => pta.plan_task_assignment_members || []
    );
    const uniqueMembers = [];
    const seenIds = new Set();
    allMembers.forEach((m) => {
      if (m.store_team_member?.id && !seenIds.has(m.store_team_member.id)) {
        seenIds.add(m.store_team_member.id);
        uniqueMembers.push(m);
      }
    });
    audience = (
      <span className="inline-flex items-center">
        {uniqueMembers.length > 0 && (
          <MemberAvatars
            members={uniqueMembers}
            roleNames={props.roleNames}
            resolveRoleId={(e) =>
              props.employeesWorking?.find(
                (w) => w.store_team_member === e.store_team_member.id
              )?.role_id
            }
          />
        )}
        {uniqueMembers.length === 0 &&
          (intradaySlots.some((a) => a.is_assigned_to_everyone) ? (
            <TeamTaskChip />
          ) : (
            <span className="inline-flex items-center text-xs font-semibold text-slate-400 border border-dashed border-slate-300 rounded-full px-2.5 py-0.5">
              Nobody pinned
            </span>
          ))}
        {!allCompleted && !props.isPastDate && (
          <PinButton onClick={() => props.handleOpenModal()} />
        )}
      </span>
    );
  } else if (
    task?.plan_task_assignments?.[0]?.plan_task_assignment_members?.length > 0
  ) {
    const pta = task.plan_task_assignments[0];
    audience = (
      <span className="inline-flex items-center">
        <MemberAvatars
          members={pta.plan_task_assignment_members}
          roleNames={props.roleNames}
          resolveRoleId={(e) =>
            props.employeesWorking?.find(
              (w) => w.store_team_member === e.store_team_member.id
            )?.role_id
          }
        />
        {pta.status !== "completed" &&
          pta.status !== "completed_late" &&
          !props.isPastDate && <PinButton onClick={() => props.handleOpenModal()} />}
      </span>
    );
  } else if (props.isAssignedToEveryone) {
    audience = <TeamTaskChip />;
  } else {
    audience = (
      <span className="inline-flex items-center">
        <span className="inline-flex items-center text-xs font-semibold text-slate-400 border border-dashed border-slate-300 rounded-full px-2.5 py-0.5">
          Nobody pinned
        </span>
        {!props.isPastDate && <PinButton onClick={() => props.handleOpenModal()} />}
      </span>
    );
  }

  // ---- status element (title row, right side) ----
  let statusEl = null;
  if (props.permissionType === "employee") {
    statusEl = (
      <TaskStatusBadge
        status={props.modalData?.status}
        lifecycleStatus={props.modalData?.lifecycle_status}
        isOverdue={props.modalData?.is_overdue}
        isLate={props.modalData?.is_late}
        paused={props.modalData?.paused}
        submitted_at={props.modalData?.submitted_at_formatted}
        submittedBy={
          (props.modalData?.plan_task_assignment_members || []).find(
            (m) => m.submitted_at
          )?.store_team_member?.full_name
        }
        size="chip"
      />
    );
  } else if (
    task?.plan_task_assignments?.length > 0 &&
    !task?.is_assignable
  ) {
    statusEl = task?.is_intraday ? (
      <MetaChip>
        {
          task.plan_task_assignments.filter(
            (a) =>
              a.intraday_slot_index != null &&
              (a.status === "completed" || a.status === "completed_late")
          ).length
        }
        /
        {
          task.plan_task_assignments.filter((a) => a.intraday_slot_index != null)
            .length
        }{" "}
        slots done
      </MetaChip>
    ) : (
      <TaskStatusBadge
        status={task.plan_task_assignments?.[0]?.status}
        lifecycleStatus={task.plan_task_assignments?.[0]?.lifecycle_status}
        isOverdue={task.plan_task_assignments?.[0]?.is_overdue}
        isLate={task.plan_task_assignments?.[0]?.is_late}
        paused={task.plan_task_assignments?.[0]?.paused}
        submitted_at={task.plan_task_assignments?.[0]?.submitted_at_formatted}
        submittedBy={
          (
            task.plan_task_assignments?.[0]?.plan_task_assignment_members || []
          ).find((m) => m.submitted_at)?.store_team_member?.full_name
        }
        size="chip"
      />
    );
  }

  return (
    <div className="space-y-3">
      {/* Title */}
      <div className="pr-8 min-w-0">
        <h3 className="text-xl font-bold text-slate-900 tracking-tight">
          {task.name}
        </h3>
        {task.description && (
          <p className="text-sm text-slate-500 mt-0.5 leading-relaxed">
            {task.description}
          </p>
        )}
      </div>

      {/* One compact row: when, who, state */}
      <div className="flex items-center gap-2 flex-wrap">
        <MetaChip>{windowText}</MetaChip>
        {task?.is_intraday && (
          <MetaChip>
            every{" "}
            {task.intraday_repeat_interval_minutes >= 60
              ? `${task.intraday_repeat_interval_minutes / 60} hr`
              : `${task.intraday_repeat_interval_minutes} min`}{" "}
            · {task.intraday_slot_count} slots
          </MetaChip>
        )}
        {audience}
        {statusEl}
      </div>

      {/* Attachments */}
      {task?.file_uploads?.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          {task.file_uploads.map((file, index) => {
            const fileType = file.name.split(".").pop();
            const open =
              fileType === "pdf"
                ? () => props.handleOpenPdfInNewTab(file.presigned_url)
                : () => {
                    props.setSelectedImage(file.presigned_url);
                    props.setShowLightbox(true);
                  };
            return (
              <button
                key={index}
                onClick={open}
                className="inline-flex items-center gap-1 text-xs font-semibold text-violet-700 bg-violet-50 hover:bg-violet-100 rounded-full px-2.5 py-1 transition-colors"
              >
                <AttachFileIcon style={{ fontSize: "13px" }} />
                {file.name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default TaskNameCardModalHeader;
